import { getRecentRoom } from "@/apis/chat";
import { useAdvancedInfiniteQuery } from "@/hooks/useAdvancedInfiniteQuery";
import { Link, useRouter } from "@/locales/routing";
import { PropsWithClassname, UnwrapPromise } from "@/types";
import { Plus } from "@/ui/icons/Plus";
import { Button, cn, SharedSelection, useDisclosure } from "@heroui/react";
import { flatten } from "lodash";
import { useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { MultiAvartar } from "../MultiAvatar";
import { ChatListBox } from "./ChatListBox";
import { useList } from "./useList";
import { useDeleteChat } from "../../_hooks/useDeleteChat";
import { ErrorConfirm } from "@/ui/Confirm/ErrorConfirm";
import { useTranslations } from "next-intl";
import { Empty } from "@/ui/Empty/Empty";
import { Pending } from "@/components/Pending";
import { useScrollElementContext } from "@/provider/ScrollElementProvider";
import { RecentGroupQueryOption } from "@/apis/query/chat";

// 群聊列表
export const GroupList = ({ className }: PropsWithClassname) => {
  const router = useRouter()
  const search = useSearchParams()
  const t = useTranslations()
  const { container } = useScrollElementContext()


  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    updateCb,
    isPending,
  } = useAdvancedInfiniteQuery(RecentGroupQueryOption)


  const users = useMemo(() => {
    return flatten(data?.pages?.
      map(page => page?.
        map(item => {
          const avatar = [
            ...(item.chat_group_robots ?? []).map(item => item.robot_info),
            ...(item.chat_group_users ?? []).map(item => item.user_info)].map(item => item?.avatar)

          // 先把 chat_record 取成确定的数组，避免 item.chat_record.length 在异常数据下抛
          // "Cannot read properties of undefined (reading 'length')"
          const records = Array.isArray(item?.chat_record) ? item.chat_record : []
          const lastRecord = records[records.length - 1]

          return ({
            name: item.chat_group?.name,
            avatar,
            chat_record: item.chat_record,
            robotsId: item.chat_robot?.robot_info?.robots_id,
            scene: lastRecord?.translate_content ||
              lastRecord?.content,
            pinned: !!item.pinned,
            id: String(item.id),
            chat_id: item.chat_id,
            isGroup: true
          })
        }
        ) ?? []) || [])
  }, [data])

  const { onPinned, values } = useList({ users, updateCb })


  const [activeId, setActiveId] = useState<number>(null!)
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure()

  const { onDeleteChat } = useDeleteChat(RecentGroupQueryOption as any, {
    onSuccess: () => {
      onClose()
      setActiveId(null!)
    }
  })

  const onDelete = (id) => {
    setActiveId(Number(id))
    onOpen()
  }

  return (
    <>
      <div className={cn("mt-[20px]", className)}>
        <Button
          className="border-[1px] font-[500] mb-[16px] text-theme-primary/70 text-[14px] border-theme-primary/10 bg-theme-primary/5"
          fullWidth
          as={Link}
          href="/create/group"
          radius="full">
          <Plus className="[&>rect]:fill-theme-primary/70" />
          <span>{t('chat.createGroup')}</span>
        </Button>


        {isPending || <ChatListBox
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          childSelector="li"
          list={users}
          emptyContent={<Empty />}
          selectedKeys={values}
          onSelectionChange={(val: SharedSelection) => {
            const chatInfo = users.find((item: any) => item?.id === val.currentKey) as any;
            if (!chatInfo) return;
            router.push(`/chats/${val.currentKey}/${chatInfo.chat_id}`);
          }}
          scrollCotnainer={container}
          avatar={(item) => (<MultiAvartar list={item.avatar} />)}

          onPinned={onPinned}
          onDelete={onDelete}
          data={data as any}
        />}

        {isFetching && <Pending size="sm" className="mt-2" />}



      </div>


      <ErrorConfirm
        title={t('chat.setting.delete.title')}
        message={t('chat.setting.delete.alert')}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onConfirm={() => onDeleteChat([activeId])}

      />
    </>

  )
}